import { useCurrentUser, useUserAuth } from '@billx/api';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LogOutIcon, MoonIcon, SettingsIcon, SunIcon } from 'lucide-react';
import React, { useState } from 'react';

interface FloatingIslandProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  showSettings?: boolean;
  onSettingsClick?: () => void;
}

export const FloatingIsland: React.FC<FloatingIslandProps> = ({
  position = 'top-right',
  showSettings = false,
  onSettingsClick,
}) => {
  const theme = useTheme();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const { logout, isLoggingOut } = useUserAuth();

  const handleLogout = async () => {
    await logout();
  };

  const { currentUser, updateUserPreference } = useCurrentUser();

  const toggleTheme = async () => {
    if (currentUser) {
      const newTheme = theme.palette.mode === 'light' ? 'dark' : 'light';

      try {
        await updateUserPreference.mutateAsync({
          theme: newTheme,
        });
      } catch (error) {
        console.error('Error updating theme:', error);
      }
    }
  };

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      zIndex: 1000,
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: 24, right: 24 };
      case 'top-left':
        return { ...baseStyles, top: 24, left: 24 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 24, right: 24 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 24, left: 24 };
      default:
        return { ...baseStyles, top: 24, right: 24 };
    }
  };

  return (
    <>
      <Box
        sx={{
          ...getPositionStyles(),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${theme.palette.divider}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.16)',
          },
        }}
      >
        {/* Theme Switcher */}
        <Tooltip
          title={`Switch to ${theme.palette.mode === 'dark' ? 'light' : 'dark'} theme`}
        >
          <IconButton
            onClick={toggleTheme}
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                transform: 'scale(1.05)',
              },
            }}
          >
            {theme.palette.mode === 'dark' ? (
              <SunIcon size={18} />
            ) : (
              <MoonIcon size={18} />
            )}
          </IconButton>
        </Tooltip>

        {/* Settings Button (optional) */}
        {showSettings && onSettingsClick && (
          <Tooltip title="Settings">
            <IconButton
              onClick={onSettingsClick}
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <SettingsIcon size={18} />
            </IconButton>
          </Tooltip>
        )}

        {/* Logout Button */}
        <Tooltip title="Logout">
          <IconButton
            onClick={() => setIsLogoutDialogOpen(true)}
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
                transform: 'scale(1.05)',
              },
            }}
          >
            <LogOutIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(20px)',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.error.main,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout? You'll need to sign in again to
            access your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setIsLogoutDialogOpen(false)}
            variant="outlined"
            disabled={isLoggingOut}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            color="error"
            disabled={isLoggingOut}
            startIcon={
              isLoggingOut ? (
                <CircularProgress size={16} />
              ) : (
                <LogOutIcon size={16} />
              )
            }
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              },
            }}
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
